/**
 * Autorização para faturar (D-209 · DS-709 · spec 03 §5 · tela 4.5).
 *
 * ## Programar não é autorizar
 *
 * `faturarEm` diz QUANDO a nota de uma parcela deve sair (0131). Para o Pedido sujeito à exigência (algum item com
 * produto marcado no catálogo), chegar a data não basta: a parcela só entra na fila com uma autorização VIGENTE, que é
 * a revisão de UMA parcela, com o conteúdo que será faturado. Autorizar não é aprovar pagamento, não é nova aprovação
 * comercial, não emite a nota e não autoriza as outras parcelas.
 *
 * E não se confunde com a situação fiscal "NF autorizada" (o retorno da prefeitura): aqui é sempre "autorização para
 * faturar".
 *
 * ## Vigência
 *
 * Vigente = a decisão ativa (não retirada nem substituída), com o MESMO conteúdo de hoje (a impressão que o servidor
 * recalcula) e `faturarEm` igual ou posterior à data autorizada. Adiar preserva; antecipar para antes da data autorizada
 * tira; voltar recupera. Retirada e substituída nunca reaparecem.
 *
 * ## Quem
 *
 * Todo usuário ativo cujo perfil de permissão tem "Autorizar faturamento". Fila compartilhada: sem filtro por
 * responsável, carteira ou criador do Pedido. Uma decisão basta.
 */
import { z } from 'zod'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'

/** Por que a parcela aguarda (a vista `v_parcela_aguardando_autorizacao`, 0213). */
export const MOTIVOS_DA_ESPERA_DE_AUTORIZACAO = ['sem_autorizacao', 'conteudo_alterado', 'programacao_antecipada'] as const
export const motivoDaEsperaDeAutorizacao = z.enum(MOTIVOS_DA_ESPERA_DE_AUTORIZACAO)
export type MotivoDaEsperaDeAutorizacao = z.infer<typeof motivoDaEsperaDeAutorizacao>

export const situacaoDaAutorizacaoParaFaturar = z.enum(['ativa', 'retirada', 'substituida', 'encerrada'])
export type SituacaoDaAutorizacaoParaFaturar = z.infer<typeof situacaoDaAutorizacaoParaFaturar>

/**
 * O estado de UMA parcela de Pedido sujeito, para quem decide o botão "Emitir NF" (4.2, 4.4, modal da 4.5).
 *
 * `vigente`: há decisão que cobre a parcela agora. `aguardando`: está no bloco da 4.5 (com o motivo).
 * `sem_pendencia`: nada a autorizar agora (parcela quitada, cancelada, sem saldo ou sem data), e também nada que
 * permita registrar nota: só `vigente` libera. Parcela de Pedido NÃO sujeito não tem estado (`null`).
 */
export const autorizacaoDaParcela = z.object({
  estado: z.enum(['vigente', 'aguardando', 'sem_pendencia']),
  motivo: motivoDaEsperaDeAutorizacao.nullable(),
  /** A decisão ATIVA, vigente ou não (a sem vigência ainda diz quem autorizou e para quando). */
  autorizacaoId: uuid.nullable(),
  faturarEmAutorizado: dataNominal.nullable(),
  autorizadaPorNome: z.string().nullable(),
  autorizadaEm: z.string().nullable(),
})
export type AutorizacaoDaParcela = z.infer<typeof autorizacaoDaParcela>

/** Uma linha do bloco "Aguardando autorização para faturar" (4.5). */
export const itemAguardandoAutorizacao = z.object({
  recebivelId: uuid,
  pedidoId: uuid,
  codigo: z.string(),
  clienteNome: z.string().nullable(),
  clienteEmpresaId: uuid.nullable(),
  perfilFaturamentoRotulo: z.string().nullable(),
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  faturarEm: dataNominal,
  vencimento: dataNominal,
  /** `faturarEm <= hoje` pelo relógio do banco (`fn_hoje`): a parcela já estaria na fila se tivesse autorização. */
  dataAlcancada: z.boolean(),
  /** Dias até `faturarEm`: negativo é atraso, zero é hoje. */
  diasAte: z.number().int(),
  motivo: motivoDaEsperaDeAutorizacao,
  valorBruto: z.string().nullable(),
  saldoAFaturar: z.string().nullable(),
  moeda: codigoDeMoeda,
  /** A decisão ativa que perdeu a vigência (conteúdo alterado ou programação antecipada). */
  anterior: z.object({
    autorizadaPorNome: z.string().nullable(),
    autorizadaEm: z.string(),
    faturarEmAutorizado: dataNominal,
  }).nullable(),
})
export type ItemAguardandoAutorizacao = z.infer<typeof itemAguardandoAutorizacao>

export const respostaAguardandoAutorizacao = z.object({
  itens: z.array(itemAguardandoAutorizacao),
  parcelas: z.number().int().nonnegative(),
  pedidos: z.number().int().nonnegative(),
  comDataAlcancada: z.number().int().nonnegative(),
  /** Quantos usuários ativos podem autorizar hoje. Zero: a pendência fica retida, nunca liberada sozinha. */
  autorizadoresElegiveis: z.number().int().nonnegative(),
  /** O pedinte pode autorizar (o Financeiro só lê). */
  podeAutorizar: z.boolean(),
  /** Os valores vieram (o pedinte vê valores); sem isso valorBruto e saldoAFaturar vêm `null`, nunca zero. */
  valoresVisiveis: z.boolean(),
})
export type RespostaAguardandoAutorizacao = z.infer<typeof respostaAguardandoAutorizacao>

/** Uma decisão do histórico da parcela, na revisão. */
export const decisaoDeAutorizacao = z.object({
  id: uuid,
  situacao: situacaoDaAutorizacaoParaFaturar,
  autorizadaPorNome: z.string().nullable(),
  autorizadaEm: z.string(),
  faturarEmAutorizado: dataNominal,
  encerradaPorNome: z.string().nullable(),
  encerradaEm: z.string().nullable(),
  motivoEncerramento: z.string().nullable(),
})
export type DecisaoDeAutorizacao = z.infer<typeof decisaoDeAutorizacao>

/**
 * A revisão de UMA parcela: exatamente o que será gravado no retrato se a pessoa autorizar.
 *
 * `impressao` é o resumo do conteúdo (sha256 de `fn_conteudo_para_faturar`, 0213) e `baseAutorizacaoId` a última
 * decisão da parcela que a pessoa viu. Os dois voltam no POST: se o conteúdo, a data ou a decisão mudaram desde que a
 * revisão abriu, o servidor recusa e devolve a revisão atual. O navegador nunca é a fonte do conteúdo.
 */
export const revisaoDaAutorizacao = z.object({
  recebivelId: uuid,
  pedidoId: uuid,
  codigo: z.string(),
  titulo: z.string().nullable(),
  clienteNome: z.string().nullable(),
  numeroParcela: z.number().int(),
  totalParcelas: z.number().int(),
  valor: z.string().nullable(),
  saldoAFaturar: z.string().nullable(),
  moeda: codigoDeMoeda,
  vencimento: dataNominal,
  faturarEm: dataNominal.nullable(),
  dataAlcancada: z.boolean(),
  perfil: z.object({
    id: uuid,
    rotulo: z.string().nullable(),
    razaoSocial: z.string().nullable(),
    documento: z.string().nullable(),
    inscricaoEstadual: z.string().nullable(),
    inscricaoMunicipal: z.string().nullable(),
    endereco: z.string().nullable(),
  }),
  instrucoesDoPerfil: z.string().nullable(),
  instrucoesComplementares: z.string().nullable(),
  itens: z.array(z.object({
    produtoNome: z.string(),
    subprodutoNome: z.string().nullable(),
    quantidade: z.string().nullable(),
    valor: z.string().nullable(),
    moeda: codigoDeMoeda,
  })),
  estado: z.enum(['vigente', 'aguardando', 'sem_pendencia']),
  motivo: motivoDaEsperaDeAutorizacao.nullable(),
  impressao: z.string().regex(/^[0-9a-f]{64}$/),
  baseAutorizacaoId: uuid.nullable(),
  /** A situação da última decisão quando a revisão abriu: a retirada muda a situação, não cria linha. */
  baseSituacao: situacaoDaAutorizacaoParaFaturar.nullable(),
  historico: z.array(decisaoDeAutorizacao),
  podeAutorizar: z.boolean(),
  podeRetirar: z.boolean(),
  valoresVisiveis: z.boolean(),
})
export type RevisaoDaAutorizacao = z.infer<typeof revisaoDaAutorizacao>

export const entradaDeAutorizacao = z.object({
  /** A impressão do conteúdo que a pessoa revisou. */
  impressao: z.string().regex(/^[0-9a-f]{64}$/, 'A revisão veio sem a impressão do conteúdo.'),
  /** A data "Faturar em" que a pessoa viu. */
  faturarEm: dataNominal,
  /** A última decisão da parcela que a pessoa viu (null: nenhuma). */
  baseAutorizacaoId: uuid.nullable(),
  /** E a situação dela naquele momento: uma retirada feita no meio também desatualiza a revisão. */
  baseSituacao: situacaoDaAutorizacaoParaFaturar.nullable().default(null),
})
export type EntradaDeAutorizacao = z.input<typeof entradaDeAutorizacao>

export const respostaDaAutorizacao = z.object({
  autorizacaoId: uuid,
  /** O mesmo comando repetido pela mesma pessoa sobre o mesmo conteúdo: nada novo foi gravado. */
  repetida: z.boolean(),
  autorizadaPorNome: z.string().nullable(),
  autorizadaEm: z.string(),
  faturarEmAutorizado: dataNominal,
  /** Para onde a parcela foi: a fila (data alcançada) ou as programadas (data futura). */
  destino: z.enum(['fila', 'programadas']),
})
export type RespostaDaAutorizacao = z.infer<typeof respostaDaAutorizacao>

export const entradaDeRetiradaDaAutorizacao = z.object({
  autorizacaoId: uuid,
  motivo: z.string().trim().min(3, 'Diga em poucas palavras por que retirar.').max(500),
})
export type EntradaDeRetiradaDaAutorizacao = z.input<typeof entradaDeRetiradaDaAutorizacao>

export const respostaDaRetiradaDaAutorizacao = z.object({
  autorizacaoId: uuid,
  repetida: z.boolean(),
})
export type RespostaDaRetiradaDaAutorizacao = z.infer<typeof respostaDaRetiradaDaAutorizacao>

/** As instruções que o Financeiro lê ao registrar a NF (e que nunca são copiadas para ela). */
export const instrucoesDeFaturamento = z.object({
  pedidoId: uuid,
  perfilFaturamentoRotulo: z.string().nullable(),
  instrucoesDoPerfil: z.string().nullable(),
  instrucoesComplementares: z.string().nullable(),
  exigeAutorizacao: z.boolean(),
})
export type InstrucoesDeFaturamento = z.infer<typeof instrucoesDeFaturamento>

// ------------------------------------------------------------------- ativação pelo catálogo

/**
 * A prévia do alcance de ligar (ou desligar) a marca de um produto.
 *
 * Ligar carimba os Pedidos existentes com item do produto (vivos e não cancelados): todas as parcelas abertas deles
 * passam a aguardar autorização, e os ciclos futuros das assinaturas vigentes também. NFs registradas, recebimentos e
 * parcelas já cobertas ficam como estão. Desligar não dispensa ninguém: só os Pedidos NOVOS deixam de ser sujeitos.
 * `impressao` resume o alcance: se ele mudar entre a prévia e a confirmação, a confirmação é recusada com a prévia nova.
 */
export const previaDaExigenciaDeAutorizacao = z.object({
  produtoId: uuid,
  produtoNome: z.string(),
  exigeHoje: z.boolean(),
  ligar: z.boolean(),
  pedidosACarimbar: z.number().int().nonnegative(),
  pedidosJaSujeitos: z.number().int().nonnegative(),
  parcelasAbertasAtingidas: z.number().int().nonnegative(),
  parcelasComDataAlcancada: z.number().int().nonnegative(),
  notasPreservadas: z.number().int().nonnegative(),
  parcelasPreservadas: z.number().int().nonnegative(),
  assinaturasComCiclosFuturos: z.number().int().nonnegative(),
  porEntidade: z.array(z.object({
    entityId: uuid,
    entidadeNome: z.string(),
    ambiente: z.string(),
    pedidos: z.number().int().nonnegative(),
    parcelasAbertas: z.number().int().nonnegative(),
    assinaturas: z.number().int().nonnegative(),
  })),
  autorizadoresElegiveis: z.number().int().nonnegative(),
  impressao: z.string().regex(/^[0-9a-f]{64}$/),
})
export type PreviaDaExigenciaDeAutorizacao = z.infer<typeof previaDaExigenciaDeAutorizacao>

export const entradaDaExigenciaDeAutorizacao = z.object({
  ligar: z.boolean(),
  impressao: z.string().regex(/^[0-9a-f]{64}$/, 'Confirme a partir da prévia do alcance.'),
})
export type EntradaDaExigenciaDeAutorizacao = z.input<typeof entradaDaExigenciaDeAutorizacao>

export const respostaDaExigenciaDeAutorizacao = z.object({
  produtoId: uuid,
  exige: z.boolean(),
  pedidosCarimbados: z.number().int().nonnegative(),
  repetida: z.boolean(),
})
export type RespostaDaExigenciaDeAutorizacao = z.infer<typeof respostaDaExigenciaDeAutorizacao>
